/*
 * Variant option-select behaviour for converted Replo sections.
 *
 * This is the revenue-critical one. Replo's runtime bound these swatches and
 * size pills to the product's variant; the conversion froze the rendered HTML,
 * which left every one of them inert with the variant id baked in at whatever
 * was selected when the page was captured. Measured on the live theme
 * (197231935865), where the Replo runtime is still active:
 *
 *   COMPRESSION      clicking "Large"  moved the variant X-Small -> Large
 *   COMPRESSION PRO  clicking "Medium" moved the variant X-Small -> Medium
 *   TENS Pod         clicking swatch 2 moved the variant White   -> Black
 *
 * Converted, those clicks did nothing, so a customer choosing Large was still
 * sent X-Small (comps1/comps3 both hardcode 55932719333753 = X-Small, and
 * tenss1/tenss3 hardcode 55123717456249 = White).
 *
 * It drives the THEME's variant picker rather than the hidden input Replo left
 * behind. That is deliberate and matches replo-atc-bridge.js: the converted
 * add-to-cart delegates to the theme's real buy button, so the theme - not
 * Replo - decides what is added. Writing Replo's own input[name="id"] would
 * look right in the DOM and still add the wrong variant. Checking the theme's
 * radio and firing `change` lets the theme recompute the variant id, the price
 * and availability, exactly as its own picker does.
 *
 * The markup contract, from Replo's own output:
 *
 *   [data-replo-option-select="true"][role="listbox"]
 *     [role="option"][aria-selected]        aria-selected drives the visual
 *                                           state via CSS (border black 1px ->
 *                                           #00b9ff 2px), so no class swapping
 *
 * Two mapping cases, both seen in these sections:
 *   - the option carries its value as text ("X-Small".."Large") -> match by text
 *   - the option is an image swatch with no text (TENS colours) -> match by
 *     position against the option's values
 *
 * When neither mapping is confident it moves aria-selected and stops. That is
 * the right outcome, not a gap: AIR's two 9-option selects are attachment
 * pickers against a single-variant product, and clicking them on the LIVE page
 * changes nothing anywhere in the document either. Inventing a variant change
 * there would be a divergence from live, not a fix.
 */
(function () {
  'use strict';

  var BOX = '[data-replo-option-select="true"]';
  var OPTION = '[role="option"]';

  function mainSection() {
    return document.querySelector('.shopify-section[id*="__main"]');
  }

  function norm(s) {
    return (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  // The theme renders one radio group per product option, named option1, option2...
  function radioGroups() {
    var scope = mainSection() || document;

    /*
     * Prefer the product form, but FALL BACK to the whole main section.
     *
     * This theme renders its variant radios OUTSIDE form.shopify-product-form - the radios
     * drive the theme's own script, which writes the chosen id into a hidden input inside
     * the form. Scoping the search to the form therefore found zero option radios, resolve()
     * returned null, and every listbox was inert while still moving aria-selected. The swatch
     * looked like it worked and the cart kept the first variant.
     *
     * Measured on TENS Pod: 2 radios in the main section, 0 inside the form.
     */
    var groups = {};
    function collect(root) {
      root.querySelectorAll('input[type="radio"][name^="option"]').forEach(function (r) {
        (groups[r.name] = groups[r.name] || []).push(r);
      });
    }

    var form = scope.querySelector('form.shopify-product-form');
    if (form) collect(form);
    if (!Object.keys(groups).length) collect(scope);
    return groups;
  }

  /*
   * Decide which theme radio group a listbox drives, and how its options line up.
   * Returns { radios, byText } or null when there is no confident answer.
   */
  function resolve(options) {
    var groups = radioGroups();
    var names = Object.keys(groups);
    if (!names.length) return null; // single-variant product: nothing to drive

    var texts = options.map(function (o) { return norm(o.textContent); });
    var labelled = texts.every(function (t) { return t.length > 0; });

    if (labelled) {
      // Prefer the group that actually contains every label. Matching by text is
      // safer than position: it survives a reordered or partially hidden listbox.
      for (var i = 0; i < names.length; i++) {
        var radios = groups[names[i]];
        var values = radios.map(function (r) { return norm(r.value); });
        var everyTextMatches = texts.every(function (t) { return values.indexOf(t) !== -1; });
        if (everyTextMatches) return { radios: radios, byText: true };
      }
    }

    // Unlabelled swatches: only positional is possible, and only when there is
    // exactly one option group with exactly as many values as there are swatches.
    if (names.length === 1 && groups[names[0]].length === options.length) {
      return { radios: groups[names[0]], byText: false };
    }
    return null;
  }

  function radioFor(mapping, option, index) {
    if (mapping.byText) {
      var want = norm(option.textContent);
      for (var i = 0; i < mapping.radios.length; i++) {
        if (norm(mapping.radios[i].value) === want) return mapping.radios[i];
      }
      return null;
    }
    return mapping.radios[index] || null;
  }

  function optionsIn(box) {
    return [].slice.call(box.querySelectorAll(OPTION));
  }

  /*
   * Find the class that MEANS "selected" on this picker.
   *
   * The generated CSS has no [aria-selected] rule at all: the ring is painted by an atomic
   * class baked onto whichever swatch was selected when the page was captured. Moving aria
   * alone therefore left the ring stuck on the first swatch while the caption, the image and
   * the cart all moved - the swatch looked unclickable even though everything behind it worked.
   *
   * The class is identified by DIFFERENCE - it is on the initially selected option and on no
   * other - rather than hardcoded, because each converted picker generates its own name. An
   * ambiguous answer returns nothing and leaves the classes untouched, which is the behaviour
   * before this existed.
   */
  function selectedClass(box) {
    var options = optionsIn(box);
    var chosen = options.filter(function (o) {
      return o.getAttribute('aria-selected') === 'true';
    })[0];
    if (!chosen) return '';

    var others = {};
    options.forEach(function (o) {
      if (o === chosen) return;
      [].slice.call(o.classList).forEach(function (c) { others[c] = true; });
    });
    var only = [].slice.call(chosen.classList).filter(function (c) { return !others[c]; });
    return only.length === 1 ? only[0] : '';
  }

  function markSelected(box, index) {
    var cls = box.dataset.optionSelectedClass;
    optionsIn(box).forEach(function (o, i) {
      o.setAttribute('aria-selected', i === index ? 'true' : 'false');
      if (cls) o.classList.toggle(cls, i === index);
    });
  }

  /*
   * Replo emits the same listbox once per breakpoint, so a page holds several
   * copies of one picker with only one visible at a time. Replo drove them from
   * shared state; keep them in step so resizing cannot reveal a stale selection.
   */
  function syncSiblings(box, index, mapping, value) {
    var container = box.closest('[data-replo-product-container]') || document;
    var count = optionsIn(box).length;
    container.querySelectorAll(BOX).forEach(function (other) {
      if (other === box || optionsIn(other).length !== count) return;
      markSelected(other, index);
      syncLabel(other, mapping, value);
    });
  }

  /*
   * Keep the "Colour: White" caption in step with the swatch.
   *
   * Replo's runtime rewrote that caption; the converted markup bakes in whichever value was
   * selected when the page was captured. Without this, choosing Black leaves a black swatch
   * under the word White - the same contradiction the image swap fixes, and just as visible.
   *
   * The caption is found by VALUE rather than by class: it is the last element outside the
   * listbox whose ENTIRE text is one of the option values. That survives the class-name churn
   * in generated markup, and matching the whole text means the "Colour:" heading beside it is
   * never mistaken for the value.
   */
  function syncLabel(box, mapping, value) {
    if (!mapping || !value) return;
    var header = box.parentElement;
    if (!header) return;
    var values = mapping.radios.map(function (r) { return norm(r.value); });
    var target = null;
    header.querySelectorAll('*').forEach(function (el) {
      if (box.contains(el) || el.contains(box)) return;
      if (el.children.length) return; // deepest text-bearing node only
      if (values.indexOf(norm(el.textContent)) === -1) return;
      target = el;
    });
    if (target) target.textContent = value;
  }

  /*
   * Swap the converted block's OWN product shot to match the chosen variant.
   *
   * Driving the theme's radio updates the theme's gallery, price and availability, but the
   * converted section carries a separate <img> that nothing else touches - so on TENS Pod the
   * swatch said "White" above a photo of the black pod.
   *
   * There are two sources for the replacement, tried in order.
   *
   * FIRST, a map the section itself declares as data-replo-colour-images, option value ->
   * asset URL. These blocks float the product on a dark panel, so they need a CUT-OUT, and a
   * product's per-variant media is a gallery shot on white — the right image is a theme asset,
   * which only the section knows about. The map is also the only source that can be trusted to
   * be framed consistently: TENS Pod's pair are aligned so the case lands in the same place in
   * both, and toggling colour changes nothing but the colour.
   *
   * SECOND, if no map is declared, the variant's featured_image from /products/<handle>.js,
   * fetched once on first use and cached. That is one small request and only after an
   * interaction; embedding the map in every section would bloat pages that never need it, and
   * reading it from the theme is not portable (ShopifyAnalytics carries variant ids but no
   * images).
   *
   * Every step is optional: no handle, no network, no featured_image, or no <img> in the
   * container all leave the picture exactly as it was. A wrong price is a bug; a picture that
   * did not update is what the page already does.
   */
  var productCache = {};

  function productData(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return productCache[handle];
  }

  // Apply a resolved URL to the block's product shot. The <source> elements have to go with
  // the srcset: a <picture> resolves its sources FIRST, so setting img.src alone leaves the
  // old image on screen at every viewport the sources cover, which is all of them.
  function applyImage(container, src) {
    // Only the product shot, not the icons and badges: take the largest image in the block.
    var imgs = [].slice.call(container.querySelectorAll('img')).filter(function (i) {
      var r = i.getBoundingClientRect();
      return r.width > 150 && r.height > 150;
    });
    if (!imgs.length) return;
    imgs.sort(function (a, b) {
      return b.getBoundingClientRect().width - a.getBoundingClientRect().width;
    });
    var img = imgs[0];
    img.removeAttribute('srcset');
    var picture = img.closest('picture');
    if (picture) {
      [].slice.call(picture.querySelectorAll('source')).forEach(function (sourceEl) {
        sourceEl.remove();
      });
    }
    img.src = src;
  }

  function declaredImages(container) {
    var raw = container.getAttribute('data-replo-colour-images');
    if (!raw) return null;
    try {
      var map = JSON.parse(raw);
      var out = {};
      Object.keys(map).forEach(function (k) { out[norm(k)] = map[k]; });
      return out;
    } catch (e) {
      return null; // a malformed map falls through to the variant image, not to a broken page
    }
  }

  function swapImage(box, radio) {
    var container = box.closest('[data-replo-product-container]');
    if (!container) return;

    var declared = declaredImages(container);
    if (declared) {
      var hit = declared[norm(radio.value)];
      if (hit) applyImage(container, hit);
      return;
    }

    var handle = container.getAttribute('data-replo-product-handle');
    if (!handle) return;

    productData(handle).then(function (data) {
      if (!data || !data.variants) return;
      // The radio's value is the option NAME ("Black"), not an id, so match on the option.
      var want = norm(radio.value);
      var variant = data.variants.filter(function (v) {
        return (v.options || []).some(function (o) { return norm(o) === want; });
      })[0];
      var src = variant && variant.featured_image && variant.featured_image.src;
      if (!src) return;

      /*
       * Only swap in a format that CAN be transparent.
       *
       * These blocks sit on a dark panel and ship a cut-out with an alpha channel. A variant's
       * featured_image is usually a GALLERY shot — TENS Pod's are .jpg, and JPEG has no alpha —
       * so swapping one in replaces the floating product with a white rectangle. That looks
       * more broken than the stale picture it replaced.
       *
       * PNG and WebP may carry alpha, so they are allowed through; JPEG never can, so it is
       * refused and the section keeps its own cut-out. Upload transparent per-variant images
       * and the swap starts working on its own, with no code change.
       */
      if (!/\.(png|webp)(\?|$)/i.test(src.split('?')[0] + (src.indexOf('?') > -1 ? '?' : ''))) return;

      applyImage(container, src);
    });
  }

  function choose(box, mapping, option, index) {
    // Resolve the radio BEFORE marking, because the caption is written from its value and
    // the sibling pickers need the same value in the same pass.
    var radio = mapping ? radioFor(mapping, option, index) : null;

    markSelected(box, index);
    syncLabel(box, mapping, radio && radio.value);
    syncSiblings(box, index, mapping, radio && radio.value);

    if (!mapping) return; // inert by design - see the header note on AIR
    if (!radio || radio.checked) return;

    radio.checked = true;
    // The theme recomputes the variant id, price and availability off this event.
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    swapImage(box, radio);
  }

  function bind(box) {
    if (box.dataset.optionSelectBound === 'true') return;
    box.dataset.optionSelectBound = 'true';

    var options = optionsIn(box);
    if (options.length < 2) return;

    // Read the ring's class off the untouched markup, before anything moves the selection.
    box.dataset.optionSelectedClass = selectedClass(box);

    /*
     * Sync the picture and caption to whatever is already selected, once, on bind.
     *
     * The converted markup bakes in whichever state the page was captured in, which need not
     * be the state the page renders in - on TENS Pod that was a BLACK pod under a swatch
     * reading "Colour: White". Syncing only on click leaves that contradiction on every first
     * view, which is the state most visitors see and never click out of.
     */
    setTimeout(function () {
      var m = mapping();
      if (!m) return;
      var i = options.findIndex(function (o) { return o.getAttribute('aria-selected') === 'true'; });
      var radio = radioFor(m, options[i < 0 ? 0 : i], i < 0 ? 0 : i);
      if (!radio) return;
      syncLabel(box, m, radio.value);
      swapImage(box, radio);
    }, 0);

    /*
     * Resolve LAZILY, on first click, and cache only a successful answer.
     *
     * This used to resolve once here at bind time and capture the result in the closure.
     * When the theme's variant radios were not in the DOM yet - the buy box renders in a
     * different section and can hydrate later - resolve() returned null and the listbox was
     * inert FOREVER, with no way to recover. And it failed silently in the worst possible
     * way: choose() still moved aria-selected, so the swatch looked like it responded while
     * the cart kept the first variant.
     *
     * Measured on TENS Pod: clicking Black left input[name=id] on the White id. Driving the
     * theme radio by hand updated it correctly, which proved the theme was fine and the
     * mapping was the missing piece.
     */
    var cached = null;
    function mapping() {
      if (!cached) cached = resolve(optionsIn(box));
      return cached;
    }

    options.forEach(function (option, index) {
      option.addEventListener('click', function (e) {
        e.preventDefault();
        choose(box, mapping(), option, index);
      });

      // role="option" with tabindex="0" implies keyboard selection
      option.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        e.preventDefault();
        choose(box, mapping(), option, index);
      });
    });
  }

  function scan() {
    document.querySelectorAll(BOX).forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // the theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', scan);
})();
